import { formatDatetime } from "../utils";

import {
  DeploymentChartType,
  DeploymentSeriesConfig,
  DeploymentTypeFilter,
  DeploymentUsageChartDataPoint,
} from "./types";

import { OrganizationDeploymentUsageItem } from "@/types/organization-usage";

const getVisibilityLabel = (isPublic: boolean) => (isPublic ? "Public" : "Private");
const DEFAULT_WORKSPACE_NAME = "Default";

const getWorkspaceLabel = (workspaceName: string) =>
  workspaceName === DEFAULT_WORKSPACE_NAME ? null : workspaceName;

const joinMetadataParts = (parts: (string | null)[]) => parts.filter(Boolean).join(" · ");

const getDeploymentLabelKey = (deployment: OrganizationDeploymentUsageItem) =>
  joinMetadataParts([
    deployment.deployment_name,
    getWorkspaceLabel(deployment.workspace_name),
    getVisibilityLabel(deployment.is_public),
  ]);

const getDeploymentMetadataLabel = (
  deployment: OrganizationDeploymentUsageItem,
  labelCounts: Record<string, number>,
) => {
  const baseLabel = joinMetadataParts([
    getWorkspaceLabel(deployment.workspace_name),
    getVisibilityLabel(deployment.is_public),
  ]);

  return labelCounts[getDeploymentLabelKey(deployment)] > 1
    ? joinMetadataParts([baseLabel, deployment.deployment_id.slice(0, 8)])
    : baseLabel;
};

export const getSeriesValue = (point: DeploymentUsageChartDataPoint, dataKey: string) =>
  typeof point[dataKey] === "number" ? Number(point[dataKey]) : 0;

export const filterDeployments = (
  deployments: OrganizationDeploymentUsageItem[],
  deploymentTypeFilter: DeploymentTypeFilter,
) =>
  deployments.filter(deployment => {
    if (deploymentTypeFilter === "public") {
      return deployment.is_public;
    }

    if (deploymentTypeFilter === "private") {
      return !deployment.is_public;
    }

    return true;
  });

export const buildDeploymentSeries = (
  deployments: OrganizationDeploymentUsageItem[],
): DeploymentSeriesConfig[] => {
  const labelCounts = deployments.reduce<Record<string, number>>((acc, deployment) => {
    const label = getDeploymentLabelKey(deployment);
    acc[label] = (acc[label] ?? 0) + 1;
    return acc;
  }, {});

  return deployments.map(deployment => ({
    dataKey: `deployment_${deployment.deployment_id.replaceAll("-", "_")}`,
    sessionDataKey: `deployment_${deployment.deployment_id.replaceAll("-", "_")}_sessions`,
    displayName: `${deployment.deployment_name} (${getDeploymentMetadataLabel(deployment, labelCounts)})`,
    deploymentName: deployment.deployment_name,
    metadataLabel: getDeploymentMetadataLabel(deployment, labelCounts),
  }));
};

export const buildDeploymentChartData = ({
  chartType,
  deployments,
  deploymentSeries,
  startDate,
  endDate,
}: {
  chartType: DeploymentChartType;
  deployments: OrganizationDeploymentUsageItem[];
  deploymentSeries: DeploymentSeriesConfig[];
  startDate?: string;
  endDate?: string;
}): DeploymentUsageChartDataPoint[] => {
  if (!startDate || !endDate || deployments.length === 0) {
    return [];
  }

  const deploymentSeriesById = new Map(
    deployments.map((deployment, index) => [deployment.deployment_id, deploymentSeries[index]]),
  );
  const dayMap = new Map<string, DeploymentUsageChartDataPoint>();

  deployments.forEach(deployment => {
    const seriesConfig = deploymentSeriesById.get(deployment.deployment_id);
    if (!seriesConfig) {
      return;
    }

    deployment.usage.forEach(usageEntry => {
      const rawDatetime = usageEntry.period_start;
      const existingPoint = dayMap.get(rawDatetime) ?? {
        rawDatetime,
        datetime: formatDatetime(rawDatetime, startDate, endDate),
        isProjected: false,
      };

      dayMap.set(rawDatetime, {
        ...existingPoint,
        [seriesConfig.dataKey]: usageEntry.total_tokens,
        [seriesConfig.sessionDataKey]: usageEntry.total_sessions,
      });
    });
  });

  const sortedDates = Array.from(dayMap.keys()).sort(
    (firstDate, secondDate) => new Date(firstDate).getTime() - new Date(secondDate).getTime(),
  );

  if (chartType === "cumulative") {
    const cumulativeTotals = deploymentSeries.reduce<Record<string, number>>(
      (acc, seriesConfig) => {
        acc[seriesConfig.dataKey] = 0;
        acc[seriesConfig.sessionDataKey] = 0;
        return acc;
      },
      {},
    );

    return sortedDates.map(rawDatetime => {
      const basePoint = dayMap.get(rawDatetime)!;
      const point: DeploymentUsageChartDataPoint = { ...basePoint };

      deploymentSeries.forEach(seriesConfig => {
        cumulativeTotals[seriesConfig.dataKey] += getSeriesValue(basePoint, seriesConfig.dataKey);
        cumulativeTotals[seriesConfig.sessionDataKey] += getSeriesValue(
          basePoint,
          seriesConfig.sessionDataKey,
        );
        point[seriesConfig.dataKey] = cumulativeTotals[seriesConfig.dataKey];
        point[seriesConfig.sessionDataKey] = cumulativeTotals[seriesConfig.sessionDataKey];
      });

      return point;
    });
  }

  return sortedDates.map(rawDatetime => {
    const basePoint = dayMap.get(rawDatetime)!;
    const point: DeploymentUsageChartDataPoint = { ...basePoint };

    deploymentSeries.forEach(seriesConfig => {
      point[seriesConfig.dataKey] = getSeriesValue(basePoint, seriesConfig.dataKey);
      point[seriesConfig.sessionDataKey] = getSeriesValue(basePoint, seriesConfig.sessionDataKey);
    });

    return point;
  });
};
